/**
 * AI Styles Management — Redesigned Orchestrator
 * Two-panel card grid layout with branded teal header and App Grid Control panel.
 * Edit/Create modal and Analytics tab unchanged.
 */

import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tab,
  Tabs,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  useTheme,
  Alert,
} from '@mui/material';
import {
  Palette as StyleIcon,
  Create as WriteIcon,
  Image as IllustrationIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  DndContext,
  DragOverlay,
} from '@dnd-kit/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useForm, Controller } from 'react-hook-form';
import AdminEditDialogShell from '../shared/AdminEditDialogShell';
import PromptField from '../shared/PromptField';

// Extracted modules
import {
  WritingStyle,
  IllustrationStyle,
  StyleFormData,
  TabPanelProps,
  EditingType,
} from './styles/types';
import { TAB_CONFIGS, CHART_COLORS } from './styles/constants';
import { useStylesData } from './styles/useStylesData';
import { useDragReorder } from './styles/useDragReorder';
import AIStylesHeader from './styles/AIStylesHeader';
import AIStylesTabBar from './styles/AIStylesTabBar';
import StyleCardGrid from './styles/StyleCardGrid';
import StyleDragOverlay from './styles/StyleDragOverlay';
import AppGridControlPanel from './styles/AppGridControlPanel';
import ModelDefaultsBar from './styles/ModelDefaultsBar';
import { useModelRegistry } from './styles/useModelRegistry';
import UnsavedChangesBar from './UnsavedChangesBar';

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`styles-tabpanel-${index}`}
      aria-labelledby={`styles-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const AIStylesManagement: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingStyle, setEditingStyle] = useState<WritingStyle | IllustrationStyle | null>(null);
  const [editingType, setEditingType] = useState<EditingType>('writing-genre');
  const [modalTabIndex, setModalTabIndex] = useState<number>(1);

  // Data hook
  const {
    writingGenres,
    writingAuthors,
    illustrationStyles,
    illustrationArtists,
    analytics,
    loading,
    popularCategory,
    handleRefresh,
    handleToggleStatus,
    handleDeleteStyle,
    handleExport,
    handleTogglePopularCategory,
    handleUpdateModelPreference,
    handleSubmitStyle,
    getDataForCategory,
  } = useStylesData();

  // Parent-level model registry so per-card dropdowns don't each fetch.
  const { models: modelCatalog } = useModelRegistry();

  // DnD hook
  const {
    hasUnsavedOrder,
    unsavedCategories,
    savingOrder,
    activeDragId,
    activeDragName,
    sensors,
    customCollisionDetection,
    getOrderedItems,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
    handleSaveOrder,
    handleDiscardOrder,
  } = useDragReorder({
    writingGenres,
    writingAuthors,
    illustrationStyles,
    illustrationArtists,
  });

  // Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<StyleFormData>({
    defaultValues: {
      name: '',
      description: '',
      prompt: '',
      category: '',
      difficulty: 'Beginner',
      artStyle: '',
      colorPalette: '',
      mood: '',
      tags: [],
      isActive: true,
    },
  });

  const toggleRowExpansion = (styleId: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(styleId)) next.delete(styleId);
      else next.add(styleId);
      return next;
    });
  };

  const handleCreateStyle = (type: 'writing' | 'illustration') => {
    reset({
      name: '',
      description: '',
      displayDescription: '',
      sampleExcerpt: '',
      prompt: '',
      category: type === 'writing' ? 'Author' : 'Illustrator',
      tags: [],
      isActive: true,
      styleCategory: type === 'illustration' ? 'style' : undefined,
      artistReference: '',
      artMovement: '',
      mediumStyle: '',
      detailLevel: 3,
      whimsyLevel: 3,
      isPremium: false,
    });
    setEditingStyle(null);
    setEditingType(type === 'writing' ? 'writing-genre' : 'illustration-style');
    setIsEditModalOpen(true);
  };

  const handleEditStyle = (style: WritingStyle | IllustrationStyle, type: 'writing' | 'illustration') => {
    const s = style as any;
    reset({
      name: s.name,
      description: s.description,
      displayDescription: s.displayDescription || '',
      sampleExcerpt: s.sampleExcerpt || '',
      prompt: s.prompt || '',
      styleDNA: s.styleDNA || '',
      transformationFormula: s.transformationFormula || '',
      visualApproach: s.visualApproach || '',
      promptConstructionFormula: s.promptConstructionFormula || '',
      truthIntegration: s.truthIntegration || '',
      category: s.category,
      tags: s.tags,
      isActive: s.isActive,
      styleCategory: s.styleCategory || 'style',
      artistReference: s.artistReference || '',
      artMovement: s.artMovement || '',
      mediumStyle: s.mediumStyle || '',
      detailLevel: s.detailLevel || 3,
      whimsyLevel: s.whimsyLevel || 3,
      isPremium: s.isPremium || false,
    });
    setEditingStyle(style);
    setEditingType(type === 'writing' ? 'writing-genre' : 'illustration-style');
    setIsEditModalOpen(true);
  };

  const onSubmit = async (data: StyleFormData) => {
    await handleSubmitStyle(data, editingStyle, editingType, () => setIsEditModalOpen(false));
  };

  // Compute header stats
  const allItems = [...writingGenres, ...writingAuthors, ...illustrationStyles, ...illustrationArtists];
  const activeCount = allItems.filter(i => i.isActive).length;
  const totalCount = allItems.length;

  // In-grid count: top 4 active per category
  const inGridCount = [writingGenres, writingAuthors, illustrationStyles, illustrationArtists]
    .reduce((sum, arr) => sum + Math.min(arr.filter(i => i.isActive).length, 4), 0);

  const tabCounts = [
    writingGenres.length,
    writingAuthors.length,
    illustrationStyles.length,
    illustrationArtists.length,
  ];

  // Get current tab's category config
  const currentTabConfig = TAB_CONFIGS[tabValue];
  const currentCategoryKey = currentTabConfig?.categoryKey;
  const currentData = currentCategoryKey ? getDataForCategory(currentCategoryKey) : [];
  const currentOrdered = currentCategoryKey ? getOrderedItems(currentCategoryKey, currentData) : [];
  const currentActiveOrdered = currentOrdered.filter(i => i.isActive);

  // Most popular check for current tab
  const isMostPopularCategory = currentTabConfig
    ? popularCategory[currentTabConfig.popularField] === currentTabConfig.popularValue
    : false;

  // Panel stats
  const panelStats = {
    inGrid: Math.min(currentActiveOrdered.length, 4),
    total: currentOrdered.length,
    inactive: currentOrdered.filter(i => !i.isActive).length,
    totalUsage: currentData.reduce((sum, i) => sum + i.usageCount, 0),
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#EDE5D6' }}>
      {/* Branded header */}
      <AIStylesHeader
        stats={{ activeCount, categoryCount: 4, inGridCount, totalCount }}
        onAddWriting={() => handleCreateStyle('writing')}
        onAddIllustration={() => handleCreateStyle('illustration')}
        onRefresh={handleRefresh}
        onExport={() => handleExport('writing')}
      />

      {/* Global xAI model defaults */}
      <ModelDefaultsBar slots={['text', 'image', 'video']} />

      {/* Tab bar (still in teal area) */}
      <AIStylesTabBar
        tabValue={tabValue}
        onChange={setTabValue}
        counts={tabCounts}
        unsavedCategories={unsavedCategories}
      />

      {/* Tab panels 0-3: Style card grids with right panel */}
      {TAB_CONFIGS.map((config, index) => {
        const catData = getDataForCategory(config.categoryKey);
        const ordered = getOrderedItems(config.categoryKey, catData);
        const activeOrdered = ordered.filter(i => i.isActive);
        const isMostPop = popularCategory[config.popularField] === config.popularValue;

        return (
          <TabPanel key={config.categoryKey} value={tabValue} index={index}>
            <DndContext
              sensors={sensors}
              collisionDetection={customCollisionDetection}
              onDragStart={(e) => handleDragStart(e, ordered)}
              onDragEnd={(e) => handleDragEnd(e, config.categoryKey, catData)}
              onDragOver={handleDragOver}
              onDragCancel={handleDragCancel}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 290px' },
                  minHeight: 'calc(100vh - 280px)',
                }}
              >
                {/* Left panel: Card grid */}
                <StyleCardGrid
                  orderedItems={ordered}
                  allItems={catData}
                  categoryKey={config.categoryKey}
                  styleType={config.styleType}
                  toggleCategory={config.toggleCategory}
                  isMostPopularCategory={isMostPop}
                  loading={loading}
                  expandedRows={expandedRows}
                  onToggle={handleToggleStatus}
                  onEdit={handleEditStyle}
                  onDelete={handleDeleteStyle}
                  onExpandToggle={toggleRowExpansion}
                  onUpdateModelPreference={handleUpdateModelPreference}
                  modelCatalog={modelCatalog}
                />

                {/* Right panel: App Grid Control */}
                <AppGridControlPanel
                  activeItems={activeOrdered}
                  stats={{
                    inGrid: Math.min(activeOrdered.length, 4),
                    total: ordered.length,
                    inactive: ordered.filter(i => !i.isActive).length,
                    totalUsage: catData.reduce((sum, i) => sum + i.usageCount, 0),
                  }}
                  isMostPopularCategory={isMostPop}
                  popularType={config.styleType}
                  popularCategory={popularCategory[config.popularField]}
                  onTogglePopularCategory={handleTogglePopularCategory}
                />
              </Box>

              <DragOverlay>
                {activeDragId != null ? <StyleDragOverlay name={activeDragName} /> : null}
              </DragOverlay>
            </DndContext>

            <UnsavedChangesBar
              visible={hasUnsavedOrder && tabValue === index}
              saving={savingOrder}
              onSave={() => handleSaveOrder(config.categoryKey)}
              onDiscard={() => handleDiscardOrder(config.categoryKey, catData)}
            />
          </TabPanel>
        );
      })}

      {/* Tab 4: Analytics (unchanged) */}
      <TabPanel value={tabValue} index={4}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" variant="body2">Writing Styles</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                        {analytics?.totalWritingStyles || 0}
                      </Typography>
                    </Box>
                    <WriteIcon sx={{ color: theme.palette.primary.main, opacity: 0.7, fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" variant="body2">Illustration Styles</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                        {analytics?.totalIllustrationStyles || 0}
                      </Typography>
                    </Box>
                    <IllustrationIcon sx={{ color: theme.palette.secondary.main, opacity: 0.7, fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" variant="body2">Avg Usage</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                        {Math.round(analytics?.avgUsageCount || 0)}
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ color: theme.palette.success.main, opacity: 0.7, fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" variant="body2">Avg Rating</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                        {(analytics?.avgRating || 0).toFixed(1)}
                      </Typography>
                    </Box>
                    <StyleIcon sx={{ color: theme.palette.warning.main, opacity: 0.7, fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Usage Over Time</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics?.usageOverTime || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="writing" fill={theme.palette.primary.main} name="Writing Styles" />
                      <Bar dataKey="illustration" fill={theme.palette.secondary.main} name="Illustration Styles" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Styles by Category</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics?.stylesByCategory || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {(analytics?.stylesByCategory || []).map((entry, i) => (
                          <Cell key={`cell-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </TabPanel>

      {/* Edit/Create Style Modal (unchanged) */}
      <AdminEditDialogShell
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        accentColor={editingType?.startsWith('writing') ? '#1A5F7A' : '#146152'}
        typeInitial={editingType?.startsWith('writing') ? 'W' : 'I'}
        title={`${editingStyle ? 'Edit' : 'Create'} ${editingType?.startsWith('writing') ? 'writing style' : 'illustration style'} — ${watch('name') || 'Untitled'}`}
        subtitle={editingType?.startsWith('writing')
          ? 'Author style · components auto-assemble into full Grok transformation prompt'
          : 'Artist style · builds the image generation prompt sent to Grok'}
        formId="style-edit-form"
        isDirty={isDirty}
        maxWidth="lg"
        showActiveToggle
        isActive={watch('isActive')}
        onToggleActive={() => {
          const current = watch('isActive');
          reset({ ...watch(), isActive: !current }, { keepDirty: true });
        }}
      >
        <form id="style-edit-form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Style name is required' }}
                  render={({ field }) => (
                    <TextField {...field} label="Style Name" fullWidth error={!!errors.name} helperText={errors.name?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: 'Description is required' }}
                  render={({ field }) => (
                    <TextField {...field} label="Description (Internal Notes)" fullWidth multiline rows={3} error={!!errors.description} helperText={errors.description?.message || 'Internal/technical description for admins'} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="displayDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Display Description (User-Facing)" fullWidth multiline rows={2} helperText="Short, user-friendly description shown in the Flutter app (50-100 chars recommended)" />
                  )}
                />
              </Grid>
              {editingType?.startsWith('writing') && (
                <Grid item xs={12}>
                  <Controller
                    name="sampleExcerpt"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Sample Excerpt (Flip Card Preview)"
                        fullWidth
                        multiline
                        rows={5}
                        helperText={`Short passage (150-250 words) demonstrating this writing style. Same reference memory recommended across all styles. Shown on card back in the mobile app. ${(field.value || '').length}/800 characters`}
                        inputProps={{ maxLength: 800 }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={modalTabIndex} onChange={(_, v) => setModalTabIndex(v)} aria-label="prompt tabs">
                    <Tab label="Complete Prompt" />
                    <Tab label="Structured Components" />
                  </Tabs>
                </Box>
                <TabPanel value={modalTabIndex} index={0}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This is a read-only preview of the complete Grok AI prompt. Edit individual components in the "Structured Components" tab.
                  </Alert>
                  <Controller
                    name="prompt"
                    control={control}
                    render={({ field }) => (
                      <PromptField value={field.value} onChange={field.onChange} onBlur={field.onBlur} inputRef={field.ref} label="COMPLETE GROK AI PROMPT (PREVIEW)" accentColor={editingType?.startsWith('writing') ? '#1A5F7A' : '#146152'} minRows={10} readOnly />
                    )}
                  />
                </TabPanel>
                <TabPanel value={modalTabIndex} index={1}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Edit individual prompt components below. The complete prompt will be automatically generated.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>Style DNA</Typography>
                      <Controller
                        name="styleDNA"
                        control={control}
                        render={({ field }) => (
                          <PromptField value={field.value} onChange={field.onChange} onBlur={field.onBlur} inputRef={field.ref} label="STYLE DNA" helperText="Core essence and defining characteristics of this style" placeholder="Enter the core essence and defining characteristics of this style..." accentColor={editingType?.startsWith('writing') ? '#1A5F7A' : '#146152'} recommendation="200-500 chars recommended" minRows={10} />
                        )}
                      />
                    </Grid>
                    {editingType.startsWith('writing') && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>Transformation Formula</Typography>
                        <Controller
                          name="transformationFormula"
                          control={control}
                          render={({ field }) => (
                            <PromptField value={field.value} onChange={field.onChange} onBlur={field.onBlur} inputRef={field.ref} label="TRANSFORMATION FORMULA" helperText="Instructions for how to transform user's input into this writing style" placeholder="Enter instructions for how to transform user's input into this writing style..." accentColor="#1A5F7A" recommendation="300-600 chars recommended" minRows={10} />
                          )}
                        />
                      </Grid>
                    )}
                    {!editingType.startsWith('writing') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>Visual Approach</Typography>
                          <Controller
                            name="visualApproach"
                            control={control}
                            render={({ field }) => (
                              <PromptField value={field.value} onChange={field.onChange} onBlur={field.onBlur} inputRef={field.ref} label="VISUAL APPROACH" helperText="How the AI should interpret visual elements and composition" placeholder="Enter how the AI should interpret visual elements and composition..." accentColor="#146152" recommendation="200-500 chars recommended" minRows={10} />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>Prompt Construction Formula</Typography>
                          <Controller
                            name="promptConstructionFormula"
                            control={control}
                            render={({ field }) => (
                              <PromptField value={field.value} onChange={field.onChange} onBlur={field.onBlur} inputRef={field.ref} label="PROMPT CONSTRUCTION FORMULA" helperText="Technical instructions for building the image generation prompt" placeholder="Enter technical instructions for building the image generation prompt..." accentColor="#146152" recommendation="300-600 chars recommended" minRows={10} />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>Truth Integration</Typography>
                      <Controller
                        name="truthIntegration"
                        control={control}
                        render={({ field }) => (
                          <PromptField value={field.value} onChange={field.onChange} onBlur={field.onBlur} inputRef={field.ref} label="TRUTH INTEGRATION" helperText="How to weave the user's personal truth/story into the output" placeholder="Enter how to weave the user's personal truth/story into the output..." accentColor={editingType?.startsWith('writing') ? '#1A5F7A' : '#146152'} recommendation="200-400 chars recommended" minRows={10} />
                        )}
                      />
                    </Grid>
                  </Grid>
                </TabPanel>
              </Grid>
              {editingType.startsWith('writing') ? (
                <Grid item xs={12}>
                  <Controller
                    name="category"
                    control={control}
                    rules={{ required: 'Type is required' }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.category}>
                        <InputLabel>Type</InputLabel>
                        <Select {...field} label="Type">
                          <MenuItem value="Category">Category</MenuItem>
                          <MenuItem value="Author">Author</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="styleCategory"
                      control={control}
                      rules={{ required: 'Style category is required' }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.styleCategory}>
                          <InputLabel>Style Category</InputLabel>
                          <Select {...field} label="Style Category">
                            <MenuItem value="style">Style (e.g., Watercolor, Vintage)</MenuItem>
                            <MenuItem value="artist">Artist (e.g., Norman Rockwell)</MenuItem>
                            <MenuItem value="technique">Technique (e.g., Pen & Ink)</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="artistReference"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Artist Reference" fullWidth placeholder="e.g., Norman Rockwell, Mary Blair" helperText="Required for 'Artist' category styles" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="artMovement"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Art Movement" fullWidth placeholder="e.g., Impressionism, Pop Art" helperText="Art movement or period (optional)" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="mediumStyle"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Medium/Style" fullWidth placeholder="e.g., watercolor, digital painting" helperText="Primary medium or technique" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="detailLevel"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Detail Level</InputLabel>
                          <Select {...field} label="Detail Level">
                            {[1, 2, 3, 4, 5].map(level => (
                              <MenuItem key={level} value={level}>
                                {level} - {level === 1 ? 'Minimal' : level === 2 ? 'Simple' : level === 3 ? 'Moderate' : level === 4 ? 'High' : 'Maximum'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="whimsyLevel"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Whimsy Level</InputLabel>
                          <Select {...field} label="Whimsy Level">
                            {[1, 2, 3, 4, 5].map(level => (
                              <MenuItem key={level} value={level}>
                                {level} - {level === 1 ? 'Realistic' : level === 2 ? 'Subtle' : level === 3 ? 'Balanced' : level === 4 ? 'Playful' : 'Fantasy'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="isPremium"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch checked={field.value || false} onChange={field.onChange} />}
                          label="Premium Style (requires subscription)"
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </form>
      </AdminEditDialogShell>
    </Box>
  );
};

export default AIStylesManagement;
